import { z } from 'zod';
import { isValidCountryPhone } from '../../shared/utils/phone-validator';
import { extractOutwardCode } from '../../shared/utils/postcode';

const MAX_AREA = 1000;

export const MAX_FLOOR_AREA = MAX_AREA;

export const UNSUPPORTED_AREA_MESSAGE =
  "We don't currently deliver to this area. Please contact us for a custom quote, or tick the box below to collect the goods yourself.";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function exceedsFloorArea(length: number | undefined, width: number | undefined): boolean {
  return (length ?? 0) * (width ?? 0) > MAX_AREA;
}

// Field-level schemas grouped by wizard step. Used independently by
// per-step validation in the wizard hook. The full form is composed below.

const step1Fields = {
  site: z.string().nullish(),
  exWorks: z.boolean(),
  siteAreaCodeId: z.number().nullish(),
  length: z
    .number({ message: 'Length is required' })
    .min(1, { message: 'Length must be 1m or more' }),
  width: z.number({ message: 'Width is required' }).min(1, { message: 'Width must be 1m or more' }),
};

type SiteFields = {
  exWorks?: boolean;
  site?: string | null;
  siteAreaCodeId?: number | null;
};

const siteSuperRefine = (d: SiteFields, ctx: z.RefinementCtx) => {
  if (d.exWorks === true) return;
  if (typeof d.site !== 'string' || d.site.trim() === '') {
    ctx.addIssue({
      path: ['site'],
      code: z.ZodIssueCode.custom,
      message: 'Site postcode is required',
    });
    return;
  }
  if (extractOutwardCode(d.site) === null) {
    ctx.addIssue({
      path: ['site'],
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid UK postcode',
    });
    return;
  }
  if (typeof d.siteAreaCodeId !== 'number' || d.siteAreaCodeId <= 0) {
    ctx.addIssue({
      path: ['site'],
      code: z.ZodIssueCode.custom,
      message: UNSUPPORTED_AREA_MESSAGE,
    });
  }
};

const step2Fields = {
  loadType: z
    .union([z.number(), z.string()])
    .nullish()
    .refine((v) => v !== null && v !== undefined && v !== '', {
      message: 'Load type is required',
    }),
  height: z
    .number({ message: 'Height is required' })
    .min(1, { message: 'Height must be 1m or more' })
    .max(10, { message: 'Height must be 10m or less' }),
  fireProtection: z.boolean(),
};

const step3Fields = {
  phoneNumber: z
    .string()
    .nullish()
    .refine((v) => isValidCountryPhone(v ?? '', 'GB'), {
      message: 'Must be a valid UK phone number',
    }),
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .refine((v) => EMAIL_REGEX.test(v), { message: 'Must be a valid email' }),
  turnstile: z
    .string()
    .nullish()
    .refine((v) => !!v, { message: 'Please complete the human verification' }),
};

const areaCheck = (d: { length?: number; width?: number }) => !exceedsFloorArea(d.length, d.width);

const areaRefineOpts = {
  path: ['width'],
  message: `Total area cannot exceed ${MAX_AREA}m²`,
};

export const step1Schema = z
  .object(step1Fields)
  .refine(areaCheck, areaRefineOpts)
  .superRefine(siteSuperRefine);

export const step2Schema = z.object(step2Fields);
export const step3Schema = z.object(step3Fields);

// Full wizard schema = all steps combined. zodResolver runs this on every
// form change so cross-step rules (area limit) stay honoured.
export const wizardFormSchema = z
  .object({ ...step1Fields, ...step2Fields, ...step3Fields })
  .refine(areaCheck, areaRefineOpts)
  .superRefine(siteSuperRefine);

export type WizardFormValues = z.infer<typeof wizardFormSchema>;

export const requestAccessSchema = z.object({
  name: z.string().min(1, { message: 'Full name is required' }),
  companyName: z.string().min(1, { message: 'Company name is required' }),
  email: z
    .string()
    .min(1, { message: 'Email address is required' })
    .refine((v) => EMAIL_REGEX.test(v), {
      message: 'Must be a valid email address',
    }),
  phoneNumber: z
    .string()
    .min(1, { message: 'Phone number is required' })
    .refine((v) => isValidCountryPhone(v, 'GB'), {
      message: 'Must be a valid UK phone number',
    }),
});

export type RequestAccessValues = z.infer<typeof requestAccessSchema>;
